/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { formatStoredDate } from "@/utils/datetime";

const PAYMENT_STATUS_GROUPS = [
  { key: "all", label: "All" },
  { key: "successful", label: "Successful" },
  { key: "pending", label: "Pending" },
  { key: "issue", label: "Needs Attention" },
  { key: "refunded", label: "Refunded" },
];

const getPaymentStatusGroup = (status) => {
  switch ((status || "").toLowerCase()) {
    case "completed":
      return "successful";
    case "pending":
    case "processing":
      return "pending";
    case "failed":
    case "cancelled":
      return "issue";
    case "refunded":
    case "partially_refunded":
      return "refunded";
    default:
      return "all";
  }
};

const getPaymentStatusPresentation = (status) => {
  const group = getPaymentStatusGroup(status);

  switch (group) {
    case "successful":
      return { label: "Successful", variant: "success" };
    case "pending":
      return { label: "Pending", variant: "warning" };
    case "issue":
      return { label: "Needs Attention", variant: "danger" };
    case "refunded":
      return { label: "Refunded", variant: "default" };
    default:
      return { label: status || "Unknown", variant: "default" };
  }
};

/**
 * Payment history table with pagination.
 */
export default function PaymentHistoryTable({
  intents = [],
  total = 0,
  page = 0,
  onPageChange,
  pageSize = 15,
}) {
  const totalPages = Math.ceil(total / pageSize);
  const [statusFilter, setStatusFilter] = React.useState("all");

  const filteredIntents = React.useMemo(() => {
    if (statusFilter === "all") {
      return intents;
    }

    return intents.filter(
      (intent) => getPaymentStatusGroup(intent.status) === statusFilter
    );
  }, [intents, statusFilter]);

  const statusCounts = React.useMemo(
    () =>
      PAYMENT_STATUS_GROUPS.reduce(
        (counts, option) => {
          if (option.key === "all") {
            counts.all = intents.length;
            return counts;
          }

          counts[option.key] = intents.filter(
            (intent) => getPaymentStatusGroup(intent.status) === option.key
          ).length;
          return counts;
        },
        { all: intents.length }
      ),
    [intents]
  );

  if (intents.length === 0 && total === 0) {
    return (
      <Card>
        <div className="p-8 text-center text-textSecondary">
          <p>No payment history yet.</p>
        </div>
      </Card>
    );
  }

  return (
    <Card>
      <div className="px-4 pt-4 pb-3 border-b border-borderDefault space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          {PAYMENT_STATUS_GROUPS.map((option) => {
            const isActive = statusFilter === option.key;
            return (
              <Button
                key={option.key}
                variant={isActive ? "default" : "outline"}
                size="sm"
                className="rounded-full"
                onClick={() => setStatusFilter(option.key)}
              >
                {option.label}
                <span className="ml-1.5 text-xs opacity-80">
                  {statusCounts[option.key] ?? 0}
                </span>
              </Button>
            );
          })}
        </div>
        <p className="text-sm text-textSecondary">
          Showing {filteredIntents.length} of {intents.length} payments on this page.
        </p>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-borderDefault">
              <th className="text-left px-4 py-3 text-textSecondary font-medium">
                Date
              </th>
              <th className="text-left px-4 py-3 text-textSecondary font-medium">
                Description
              </th>
              <th className="text-left px-4 py-3 text-textSecondary font-medium">
                Type
              </th>
              <th className="text-left px-4 py-3 text-textSecondary font-medium">
                Gateway
              </th>
              <th className="text-right px-4 py-3 text-textSecondary font-medium">
                Amount
              </th>
              <th className="text-center px-4 py-3 text-textSecondary font-medium">
                Status
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredIntents.length > 0 ? (
              filteredIntents.map((intent) => {
                const statusPresentation = getPaymentStatusPresentation(intent.status);

                return (
              <tr
                key={intent.id}
                className="border-b border-borderDefault/50 hover:bg-secondary/5 transition-colors"
              >
                <td className="px-4 py-3 text-textSecondary whitespace-nowrap">
                  {formatStoredDate(intent.created_at, {
                    month: "short",
                    day: "numeric",
                    year: "numeric",
                  }, "-")}
                </td>
                <td className="px-4 py-3 text-textPrimary max-w-xs truncate">
                  {intent.description || "Payment"}
                </td>
                <td className="px-4 py-3 text-textSecondary capitalize">
                  {(intent.reference_type || "").replace(/_/g, " ") || "—"}
                </td>
                <td className="px-4 py-3 text-textSecondary capitalize">
                  {intent.gateway || "—"}
                </td>
                <td className="px-4 py-3 text-right text-textPrimary font-medium whitespace-nowrap">
                  ${Number(intent.amount).toFixed(2)}{" "}
                  <span className="text-textSecondary text-xs uppercase">
                    {intent.currency || "USD"}
                  </span>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex flex-col items-center gap-1">
                    <Badge variant={statusPresentation.variant}>
                      {statusPresentation.label}
                    </Badge>
                    <span className="text-xs text-textSecondary capitalize">
                      {(intent.status || "unknown").replace(/_/g, " ")}
                    </span>
                  </div>
                </td>
              </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-textSecondary">
                  No payments match this filter on the current page.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between px-4 py-3 border-t border-borderDefault">
          <p className="text-sm text-textSecondary">
            Showing {page * pageSize + 1}–
            {Math.min((page + 1) * pageSize, total)} of {total}
          </p>
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              disabled={page === 0}
              onClick={() => onPageChange(page - 1)}
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <Button
              variant="ghost"
              size="sm"
              disabled={page >= totalPages - 1}
              onClick={() => onPageChange(page + 1)}
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}
    </Card>
  );
}
